import {isFeatureEnabled} from 'feature-flags';
import {openDialog, TableRowAction} from 'platform/components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  DefinitionWithTemplatesResponseBody,
  TemplateResponseBody,
  useHideTemplateMutation,
  useLazyDownloadTemplateSourceV3Query,
  useLazyDownloadTemplateV3Query,
  useRemoveTemplateMutation,
  useSetPrimaryAuthProfileMutation,
  useSetPrimaryTemplateV3Mutation,
  useShowTemplateMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {useCreateTemplateCopy} from '../../DocumentTemplateEditor/hooks/useCreateTemplateCopy';
import {getDocumentTemplateEditorPath} from '../../DocumentTemplateEditor/utils/getDocumentTemplateEditorPath';
import {EditTemplateForm} from '../components/EditTemplateForm';

export const useTemplateActions = (
  definitionType: DefinitionWithTemplatesResponseBody['type'],
  siblingTitles: string[]
) => {
  const [showTemplate] = useShowTemplateMutation();
  const [hideTemplate] = useHideTemplateMutation();
  const [deleteTemplate] = useRemoveTemplateMutation();
  const [setTemplateAsPrimary] = useSetPrimaryTemplateV3Mutation();
  const [setPrimaryAuthProfile] = useSetPrimaryAuthProfileMutation();
  const [downloadTemplate] = useLazyDownloadTemplateV3Query();
  const [downloadTemplateSource] = useLazyDownloadTemplateSourceV3Query();
  const {createTemplateCopy} = useCreateTemplateCopy();
  const navigate = useNavigate();

  const getTemplateActions = (template: TemplateResponseBody & {isPrimary: boolean}) => {
    const isSystem = template.type === 'system';

    const handleSetPrimary = () => {
      if (isNotNil(template.authorizationProfileId)) {
        setPrimaryAuthProfile({
          authorizationProfileId: template.authorizationProfileId!,
          templateId: template.id,
        })
          .unwrap()
          .catch(handleApiError);
      } else {
        setTemplateAsPrimary({
          templateId: template.id,
        })
          .unwrap()
          .catch(handleApiError);
      }
    };

    const handleDelete = () =>
      deleteTemplate({templateId: template.id}).unwrap().catch(handleApiError);

    const handleHide = () => hideTemplate({templateId: template.id}).unwrap().catch(handleApiError);

    const handleShow = () => showTemplate({templateId: template.id}).unwrap().catch(handleApiError);

    const handleDownload = () =>
      downloadTemplate({templateId: template.id})
        .unwrap()
        .then((file) => downloadBlobFile(file, `${template.title}.${definitionType}`))
        .catch(handleApiError);

    const handleEdit = () =>
      openDialog(
        <EditTemplateForm
          template={template}
          authorizationProfileId={template.authorizationProfileId}
          documentCode={template.documentCode}
          documentType={definitionType}
        />,
        {
          title: i18n.t('entity.documents.labels.uploadEditTemplateDialogTitle'),
        }
      );

    const handleOpenEditor = () => navigate(getDocumentTemplateEditorPath(template.id));
    const handleCreateCopy = () =>
      downloadTemplateSource({templateId: template.id})
        .unwrap()
        .then((source) => createTemplateCopy(template, source, siblingTitles))
        .catch(handleApiError);

    const editorActionTitle = match(isSystem)
      .with(true, () => i18n.t('entity.documentTemplateEditor.actions.viewInEditor'))
      .with(false, () => i18n.t('entity.documentTemplateEditor.actions.openEditor'))
      .exhaustive();

    return buildArray<TableRowAction>()
      .add({
        icon: 'file/download',
        title: `${i18n.t('entity.document.labels.downloadAs')} ${definitionType}`,
        onClick: handleDownload,
      })
      .when(!template.isPrimary && !template.isHidden, {
        icon: 'action/check_circle',
        title: i18n.t('entity.document.actions.setPrimary'),
        severity: 'success',
        onClick: handleSetPrimary,
      })
      .when(!template.isPrimary && !template.isHidden && template.canBeHidden, {
        icon: 'action/visibility_off',
        title: i18n.t('general.actions.hide'),
        onClick: handleHide,
      })
      .when(template.isHidden, {
        icon: 'action/visibility',
        title: i18n.t('general.actions.unhide'),
        onClick: handleShow,
      })
      .when(isFeatureEnabled(featureFlags.DOCUMENT_TEMPLATE_EDITOR) && definitionType === 'html', {
        icon: 'action/open_in_new',
        title: editorActionTitle,
        onClick: handleOpenEditor,
      })
      .when(isFeatureEnabled(featureFlags.DOCUMENT_TEMPLATE_EDITOR) && definitionType === 'html', {
        icon: 'content/copy',
        title: i18n.t('entity.documentTemplateEditor.actions.createCopy'),
        onClick: handleCreateCopy,
      })
      .whenNot(isSystem, {
        icon: 'image/edit',
        title: i18n.t('general.actions.edit'),
        onClick: handleEdit,
      })
      .when(template.canBeDeleted, {
        icon: 'action/delete',
        title: i18n.t('general.actions.delete'),
        severity: 'danger',
        onClick: handleDelete,
      });
  };

  return [getTemplateActions] as const;
};

const downloadBlobFile = (file: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([file]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};
